import { useEffect, useRef, useState } from 'react';
import type { PlayerProfile, SlotDemoScene } from '@goodtime/engine';
import { funnelBeacon } from '../api/funnel';
import { claimWeeklyGift, fetchWeeklyGift } from '../api/wallet';
import { clearGiftLink, readGiftLink, type GiftLinkStyle } from '../giftLink';
import { loadGameArt } from './minigames/gameArt';

export interface WeeklyGiftLinkProps {
  playerId: string | null;
  /** The slot is up, the age check answered and the wallet synced. */
  ready: boolean;
  scene: () => SlotDemoScene | null;
  profile: PlayerProfile;
}

/**
 * The weekly gift behind a Facebook post's link (producer 2026-11-04: "Click
 * here for your free gift", "a mix of spin the wheel or here's your gift"):
 * once the slot is ready it asks the server for this week's gift and shows
 * it over the slot, as a box to open or a wheel to spin. The server holds
 * the week and picks the prize; this only shows it. Nothing shows when the
 * gift is switched off or can't be read. Renders nothing itself.
 */
export function WeeklyGiftLink({ playerId, ready, scene, profile }: WeeklyGiftLinkProps) {
  const [style] = useState<GiftLinkStyle | null>(readGiftLink);
  // Which post the link came from, for the funnel (the visit's own tags are first-touch only).
  const [post] = useState<string>(() => new URLSearchParams(window.location.search).get('utm_content')?.slice(0, 60) ?? '');
  const started = useRef(false);
  useEffect(() => {
    const slot = scene();
    if (!style || started.current || !playerId || !ready || !slot) return;
    started.current = true;
    clearGiftLink();
    void (async () => {
      try {
        const status = await fetchWeeklyGift(playerId);
        if (!status.enabled || !status.prizes?.length) return;
        // The wheel wears the Lucky Wheel's painted rim, hub and pointer, and its sounds.
        if (style === 'wheel') await loadGameArt('wheel');
        funnelBeacon('weekly_gift', { playerId, detail: `${status.available ? 'shown' : 'taken'}:${style}:${post}` });
        await slot.playWeeklyGift({
          style,
          prizes: status.prizes,
          taken: status.available ? null : (status.prize ?? null),
          claim: async () => {
            try {
              const r = await claimWeeklyGift(playerId);
              profile.reconcileBalance(r.wallet.coins);
              profile.adoptGems(r.wallet.gems);
              if (r.claimed && r.prize) {
                funnelBeacon('weekly_gift', { playerId, detail: `opened:${style}:${post}:${r.prize.index}` });
              }
              return r.prize ?? null;
            } catch {
              return null;
            }
          },
        });
      } catch {
        // No gift this time: the game carries on as usual.
      }
    })();
  }, [style, post, playerId, ready, scene, profile]);
  return null;
}
